// Mock data for CORE IT Group website

export const companyInfo = {
  name: "CORE IT Group",
  tagline: "Practical, Security-Focused IT & Cybersecurity Services",
  description: "CORE IT Group delivers practical, security-focused IT, cybersecurity, and risk management services that help organizations operate securely, comply with regulations, and scale with confidence.",
  email: "info@coreitgrp.com",
  careersEmail: "careers@coreitgrp.com",
  phone: "(XXX) XXX-XXXX",
  location: "United States (Remote Services Available)"
};

export const whatWeDo = [
  {
    id: 1,
    title: "Cybersecurity & IT Risk Management",
    icon: "Shield",
    link: "/services#cybersecurity",
    description: "Risk assessments, control gap analysis, and security maturity assessments to strengthen your security posture."
  },
  {
    id: 2,
    title: "Governance, Risk & Compliance (GRC)",
    icon: "Scale",
    link: "/services#governance",
    description: "NIST CSF, ISO 27001/27701 alignment, policy development, and compliance readiness assessments."
  },
  {
    id: 3,
    title: "Third-Party / Vendor Risk Management (TPRM)",
    icon: "Users",
    link: "/services#third-party",
    description: "Vendor risk assessments, SOC report reviews, and sub-processor risk analysis."
  },
  {
    id: 4,
    title: "Cloud & Application Security Advisory",
    icon: "Cloud",
    link: "/services#cloud",
    description: "SaaS security reviews, identity management advisory, and data protection strategy."
  },
  {
    id: 5,
    title: "Audit Readiness (SOC 2, ISO 27001, NIST)",
    icon: "ClipboardCheck",
    link: "/services#audit",
    description: "SOC 2 Type I & II readiness, ISO 27001 support, and internal audit preparation."
  }
];

export const whyUs = [
  {
    id: 1,
    title: "Risk-based, not checkbox-driven",
    description: "We focus on real risks that matter to your business, not just compliance checkboxes."
  },
  {
    id: 2,
    title: "Business-aligned security outcomes",
    description: "Security strategies that support your business goals and growth objectives."
  },
  {
    id: 3,
    title: "Clear communication for all stakeholders",
    description: "Technical and non-technical stakeholders receive clear, actionable insights."
  }
];

export const aboutUs = {
  intro: "CORE IT Group was founded to bridge the gap between technical security controls and real business risk. We partner with organizations to identify what matters most, strengthen security posture, and create defensible, repeatable risk management processes.",
  approach: "Our approach combines industry standards (NIST, ISO, CIS, SOC 2) with hands-on experience across cloud, application, and enterprise environments.",
  mission: "Our mission is to empower organizations with clear, actionable, and scalable IT and cybersecurity solutions that reduce risk, strengthen trust, and enable sustainable growth.",
  vision: "To be a trusted partner for organizations seeking practical, resilient, and future-ready security and risk management solutions."
};

export const coreValues = [
  {
    id: 1,
    title: "Integrity",
    description: "We operate transparently and ethically",
    icon: "Heart"
  },
  {
    id: 2,
    title: "Clarity",
    description: "We translate complexity into understanding",
    icon: "Lightbulb"
  },
  {
    id: 3,
    title: "Practicality",
    description: "We focus on controls that actually reduce risk",
    icon: "Target"
  },
  {
    id: 4,
    title: "Collaboration",
    description: "We work as an extension of our clients' teams",
    icon: "Handshake"
  },
  {
    id: 5,
    title: "Continuous Improvement",
    description: "We adapt as threats and technologies evolve",
    icon: "TrendingUp"
  }
];

export const services = [
  {
    id: 1,
    category: "Cybersecurity & IT Risk Management",
    anchor: "cybersecurity",
    icon: "ShieldCheck",
    items: [
      "Risk assessments and risk profiling",
      "Control gap analysis and remediation guidance",
      "Security maturity assessments"
    ]
  },
  {
    id: 2,
    category: "Governance, Risk & Compliance (GRC)",
    anchor: "governance",
    icon: "Scale",
    items: [
      "NIST CSF, ISO 27001/27701 alignment",
      "Policy and procedure development",
      "Compliance readiness and gap assessments"
    ]
  },
  {
    id: 3,
    category: "Third-Party Risk Management (TPRM)",
    anchor: "third-party",
    icon: "Network",
    items: [
      "Vendor risk assessments",
      "SOC report and penetration test reviews",
      "Sub-processor and cloud risk analysis"
    ]
  },
  {
    id: 4,
    category: "Cloud & Application Security",
    anchor: "cloud",
    icon: "CloudCog",
    items: [
      "SaaS and cloud security reviews",
      "Identity & access management (SSO/MFA) advisory",
      "Data protection and encryption strategy"
    ]
  },
  {
    id: 5,
    category: "Audit & Assurance Readiness",
    anchor: "audit",
    icon: "FileCheck",
    items: [
      "SOC 2 Type I & II readiness",
      "ISO 27001 readiness support",
      "Internal audit preparation"
    ]
  }
];

export const industries = [
  { id: 1, name: "Financial Services", icon: "Landmark" },
  { id: 2, name: "Technology & SaaS", icon: "Cpu" },
  { id: 3, name: "Healthcare", icon: "Stethoscope" },
  { id: 4, name: "Manufacturing", icon: "Factory" },
  { id: 5, name: "Professional Services", icon: "Briefcase" },
  { id: 6, name: "Public & Regulated Sectors", icon: "Building2" }
];

export const careers = {
  intro: "At CORE IT Group, we value curiosity, accountability, and continuous learning.",
  benefits: [
    "Collaborative environment",
    "Exposure to diverse industries and challenges",
    "Emphasis on professional growth and certifications"
  ],
  openings: [
    {
      id: 1,
      title: "Cybersecurity Analyst",
      type: "Full-time",
      location: "Remote"
    },
    {
      id: 2,
      title: "IT Risk & GRC Consultant",
      type: "Full-time",
      location: "Remote"
    },
    {
      id: 3,
      title: "Vendor Risk Analyst",
      type: "Full-time",
      location: "Remote"
    }
  ]
};

export const faqData = [
  {
    id: 1,
    question: "Do you offer remote services?",
    answer: "Yes, we provide services remotely and on-site as needed."
  },
  {
    id: 2,
    question: "Do you help with compliance certifications?",
    answer: "We support readiness and preparation but do not issue certifications."
  },
  {
    id: 3,
    question: "Can you tailor services to our organization?",
    answer: "Absolutely. Our approach is customized based on risk and business needs."
  }
];

export const legalPages = {
  privacy: {
    title: "Privacy Policy",
    content: `CORE IT Group is committed to protecting your privacy.

We collect limited personal information (such as name, email address, and phone number) when you voluntarily submit it through our website. This information is used solely to respond to inquiries and provide services.

We do not sell or share personal information with third parties except as required by law or to deliver requested services.`
  },
  terms: {
    title: "Terms of Use",
    content: `By accessing this website, you agree to use it for lawful purposes only. All content is the property of CORE IT Group and may not be reproduced without permission.`
  },
  cookies: {
    title: "Cookie Policy",
    content: `Our website may use cookies to improve user experience and analyze site traffic. You may disable cookies through your browser settings.`
  },
  accessibility: {
    title: "Accessibility Statement",
    content: `CORE IT Group is committed to ensuring digital accessibility for all users. If you experience difficulty accessing any content, please contact us for assistance.`
  },
  disclaimer: {
    title: "Disclaimer",
    content: `Information provided on this website is for general informational purposes only and does not constitute legal, regulatory, or professional advice.`
  }
};
